import moment from 'moment';
import BoardConst from './constants/board';

/**
 * Context describing how a column's items are projected into the rendered list.
 *
 * `issueTypeOptionsCount` is the number of activity issue-type filter options
 * (from getActivityIssueTypesFilterOptions). It is injected by the caller so
 * this module stays free of the UI/i18n dependencies that helper pulls in and
 * remains a pure, unit-testable function.
 */
export interface ColumnVisibilityContext {
  boardView: unknown;
  boardType: unknown;
  boardFilters: any;
  issueTypeOptionsCount?: number;
}

/**
 * Per-item filter predicate shared by the board renderer (ColumnList) and the
 * drag-reorder action (column.actions). Both MUST use this single source of
 * truth so the reorder operates on exactly the projection the user sees.
 *
 * This mirrors the filtering ColumnList applies to each column's `data` on the
 * Activity board view. Comparisons are intentionally loose (`==`) to match the
 * original behaviour and the mixed string/number shapes coming from the API.
 */
export const isColumnItemVisible = (item: any, ctx: ColumnVisibilityContext): boolean => {
  const { boardType } = ctx;
  const boardFilters = ctx.boardFilters || {};
  const {
    subaction,
    done_items: doneItemsRaw = 0,
    hide_done_items: hideDoneItems = 0,
    status = [],
    rating = [],
    priority = [],
    assignee = '',
    tags = [],
    issueTypes = [],
  } = boardFilters;
  const doneItems = parseInt(doneItemsRaw, 10);
  const issueTypeOptionsCount =
    typeof ctx.issueTypeOptionsCount === 'number' ? ctx.issueTypeOptionsCount : issueTypes.length;
  const selectedAssignees = Array.isArray(assignee)
    ? assignee.map((id: any) => String(id))
    : String(assignee || '')
      .split(',')
      .map((id: string) => id.trim())
      .filter((id: string) => id !== '');
  let isvalid = true;

  if (item.state == 0) {
    if (boardType == BoardConst.WS_BOARD_TYPE_CUSTOM && hideDoneItems == '1') {
      isvalid = false;
    } else if (!Number.isNaN(doneItems)) {
      isvalid = false;
      if (item.due_date > 0) {
        const diff = moment(moment().format('YYYY-MM-DD 00:00:00')).diff(item.due_date * 1000, 'days');
        if (!Number.isNaN(diff) && diff >= 0) {
          isvalid = diff <= doneItems;
        }
      }
    }
  }

  const isCustomBoard = boardType == BoardConst.WS_BOARD_TYPE_CUSTOM;
  if (isvalid && isCustomBoard && status.length > 0 && status.length != 4) {
    isvalid = !status.includes(`${item.state}`);
  }
  if (isvalid && rating.length > 0 && rating.length != 3) {
    isvalid = !rating.includes(`${item.rag}`);
  }
  if (isvalid && priority.length > 0 && priority.length != 3) {
    isvalid = !priority.includes(`${item.priority}`);
  }
  if (isvalid && issueTypes.length > 0 && issueTypes.length != issueTypeOptionsCount) {
    isvalid = !issueTypes.includes(`${item.type}`);
  }
  if (isvalid && tags.length > 0) {
    const selectedTags = tags.map((tag: any) => String(tag));
    const aiTagIds = (item.aiTags || []).map((tag: any) => String(tag.id));
    isvalid = selectedTags.some((tagId: string) => aiTagIds.includes(tagId));
  }
  if (isvalid && selectedAssignees.length > 0) {
    isvalid = selectedAssignees.includes(String(item.owner));
  }
  if (isvalid && subaction == '0') {
    isvalid = !item.parent;
  }
  return !!isvalid;
};

/**
 * Whether the Activity-board item filters are in effect. When false the
 * rendered list equals the full column data, so react-beautiful-dnd indices
 * already line up with the underlying array and no remapping is needed.
 */
export const isActivityFilterActive = (ctx: ColumnVisibilityContext): boolean =>
  ctx.boardView == BoardConst.BOARD_VIEW_ACTIVITY;

/**
 * Absolute indices, in order, of the items in `data` that are visible in the
 * rendered list. When no filtering is active this is simply every index.
 */
const visibleSlotIndexes = (data: any[], ctx: ColumnVisibilityContext): number[] => {
  if (!isActivityFilterActive(ctx)) {
    return data.map((unused: any, i: number) => i);
  }
  const slots: number[] = [];
  data.forEach((item: any, i: number) => {
    if (isColumnItemVisible(item, ctx)) {
      slots.push(i);
    }
  });
  return slots;
};

/**
 * Reorder an item within a single column. `sourceVisibleIndex` /
 * `destVisibleIndex` are positions in the RENDERED (filtered) list, exactly as
 * react-beautiful-dnd reports them.
 *
 * Returns a NEW data array in which the visible items appear in their new order
 * while filtered-out items (e.g. completed action items hidden by the Done
 * filter) stay anchored to their original absolute slots. This is the fix for
 * MR-8165: previously the source index was resolved against the full array but
 * the destination index was applied as a rendered-list position, so a card
 * dropped at one slot jumped to a different one.
 */
export const reorderWithinColumnData = (
  data: any[],
  ctx: ColumnVisibilityContext,
  sourceVisibleIndex: number,
  destVisibleIndex: number,
): any[] => {
  const slots = visibleSlotIndexes(data, ctx);
  const visible = slots.map((slot) => data[slot]);
  if (sourceVisibleIndex < 0 || sourceVisibleIndex >= visible.length) {
    return data.slice();
  }
  const [moved] = visible.splice(sourceVisibleIndex, 1);
  const dest = Math.max(0, Math.min(destVisibleIndex, visible.length));
  visible.splice(dest, 0, moved);
  const result = data.slice();
  slots.forEach((slot, k) => {
    result[slot] = visible[k];
  });
  return result;
};

/**
 * Absolute index in `data` of the visible item at `visibleIndex` — used to
 * remove the dragged item from its source column on a cross-column move.
 * Returns -1 when out of range.
 */
export const fullIndexOfVisibleItem = (
  data: any[],
  ctx: ColumnVisibilityContext,
  visibleIndex: number,
): number => {
  const slots = visibleSlotIndexes(data, ctx);
  if (visibleIndex < 0 || visibleIndex >= slots.length) {
    return -1;
  }
  return slots[visibleIndex];
};

/**
 * Absolute index in `data` at which to insert a dropped item so it lands at
 * `destVisibleIndex` within the destination column's RENDERED list. Used for
 * the insertion side of a cross-column move.
 */
export const fullInsertIndexForVisibleIndex = (
  data: any[],
  ctx: ColumnVisibilityContext,
  destVisibleIndex: number,
): number => {
  const slots = visibleSlotIndexes(data, ctx);
  if (destVisibleIndex < slots.length) {
    return slots[destVisibleIndex];
  }
  // Dropping at/after the end of the visible list: place it just after the
  // last visible item (or at the very end when the column has none visible).
  if (slots.length) {
    return slots[slots.length - 1] + 1;
  }
  return data.length;
};
